'use client';

/**
 * Первый экран.
 *
 * Все цифры — из `GET /api/dashboard/metrics` и соседних аналитических ручек;
 * ни одна не складывается на клиенте. Границы периода тоже приходят с
 * сервера: он считает их в поясе тенанта, и подпись собрана из его ответа.
 */

import React from 'react';
import MetricTiles from './MetricTiles';
import MissedReasons from './MissedReasons';
import RecentCallsTable from './RecentCallsTable';
import ReportFilterBar from '@/components/reports/ReportFilterBar';
import ScoreDynamicsChart from '@/components/reports/ScoreDynamicsChart';
import TopViolationsList from '@/components/reports/TopViolationsList';
import { ReportError, ReportLoading } from '@/components/reports/ReportState';
import { useReportFilters } from '@/components/reports/useReportFilters';
import { useDashboardMetrics, useScoreDynamics, useTopViolations } from '@/lib/api/hooks';

export default function DashboardContent() {
  const { filters, patch, preset } = useReportFilters();

  // `compare` включено всегда: плитка без сравнения не говорит, стало лучше
  // или хуже, а именно это с первого экрана и спрашивают.
  const metrics = useDashboardMetrics(filters, true);
  const dynamics = useScoreDynamics(filters, 'day', 'unit');
  const violations = useTopViolations(filters, 6);

  // На сборках до 0.29 причин пропуска нет — от этого зависит вся раскладка
  // нижних рядов, поэтому считаем один раз.
  const hasReasons = Boolean(metrics.data?.missed.reason);

  return (
    <div className="space-y-6">
      <ReportFilterBar
        filters={filters}
        patch={patch}
        preset={preset}
        period={metrics.data?.period}
        showEmployee
      />

      {metrics.isPending && <ReportLoading height={240} />}
      {metrics.error && <ReportError error={metrics.error} height={240} />}
      {metrics.data && <MetricTiles data={metrics.data} />}

      {/* Причины пропуска — рядом с плитками, но отдельным блоком: судьба и
          причина разные вопросы, и слитые в одну карточку они читаются как
          разбивка одного числа. */}
      {metrics.data?.missed.reason && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          <div className="xl:col-span-2">
            <MissedReasons
              reason={metrics.data.missed.reason}
              total={metrics.data.missed.count}
              filters={filters}
            />
          </div>
          <div className="xl:col-span-3 card p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-1">Частые нарушения</h2>
            <p className="text-xs text-slate-400 mb-4">
              Доля — от проверок, где правило дало вердикт
            </p>
            {violations.isPending && <ReportLoading height={220} />}
            {violations.error && <ReportError error={violations.error} height={220} />}
            {violations.data && <TopViolationsList data={violations.data} />}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Когда нарушения уехали в верхний ряд, график занимает всю ширину:
            пустая треть рядом с ним читалась бы как не загрузившийся блок. */}
        <div className={`card p-5 ${hasReasons ? 'xl:col-span-5' : 'xl:col-span-3'}`}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Динамика среднего балла</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                День без оценённых звонков — разрыв линии, а не ноль
              </p>
            </div>
            {/* Целевой балл подписан в шапке, а не ярлыком на самой линии:
                ярлык налезал на график в узкой карточке. */}
            {dynamics.data && (
              <span className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full bg-green-600" />
                Целевой: {dynamics.data.target}
              </span>
            )}
          </div>
          {dynamics.isPending && <ReportLoading height={220} />}
          {dynamics.error && <ReportError error={dynamics.error} height={220} />}
          {dynamics.data && <ScoreDynamicsChart data={dynamics.data} height={220} />}
        </div>

        {/* На сборках без `missed.reason` нарушения остаются здесь: пустой
            столбец рядом с графиком выглядел бы поломкой. */}
        {!metrics.data?.missed.reason && (
          <div className="xl:col-span-2 card p-5">
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-slate-800">Частые нарушения</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Доля — от проверок, где правило дало вердикт
              </p>
            </div>
            {violations.isPending && <ReportLoading height={220} />}
            {violations.error && <ReportError error={violations.error} height={220} />}
            {violations.data && <TopViolationsList data={violations.data} />}
          </div>
        )}
      </div>

      <RecentCallsTable filters={filters} />
    </div>
  );
}
